import { Intercom, update } from '@intercom/messenger-js-sdk';
import { useAuthStore } from '@tundr/auth';
import { useWizardStore } from '@tundr/wizard-stepper';
import { computed, watch } from 'vue';
import { isInternalHrUserByEmail } from '@core/utils/hr/hr.utils';
import { isAuthFlowNamespace } from '@modules/auth/config/auth-flows';
import { ExternalService, isServiceEnabled } from '../external-services';
import { ENV_VARS } from '../env';

const INTERCOM_REGION = 'eu';
const INTERCOMP_APP_NAME = 'HR';
const VERTICAL_PADDING_DEFAULT = 0;
const VERTICAL_PADDING_WIZARD = 100;

const getVerticalPadding = (hasActiveWizard: boolean) =>
  hasActiveWizard ? VERTICAL_PADDING_WIZARD : VERTICAL_PADDING_DEFAULT;

export const useSupportChat = () => {
  const authStore = useAuthStore();
  const wizardStore = useWizardStore();

  const hasActiveWizard = computed(() =>
    Object.keys(wizardStore.steppers).some(
      (namespace) => !isAuthFlowNamespace(namespace),
    ),
  );

  const initSupportChat = () => {
    if (!isServiceEnabled(ExternalService.Intercom)) return;
    if (!ENV_VARS.intercom.appId) return;

    const verticalPadding = getVerticalPadding(hasActiveWizard.value);

    if (authStore.authData?.user && authStore.selectedCompany) {
      const company = authStore.selectedCompany;
      Intercom({
        app_id: ENV_VARS.intercom.appId,
        region: INTERCOM_REGION,
        vertical_padding: verticalPadding,
        name: `${authStore.authData?.user.firstName} ${authStore.authData?.user.lastName}`,
        user_id: company.hrId,
        phone: authStore.authData?.user.phoneNumber ?? null,
        email: company.email,
        // send date in timestamp in seconds
        createdAt: Math.floor(
          new Date(authStore.authData?.user.createdAt).getTime() / 1000,
        ),
        company: {
          company_id: company.id,
          name: company.name,
          plan: company.currentWelfarePlan,
          channel: company.companyChannel?.name ?? null,
        },
        Product: INTERCOMP_APP_NAME,
        isInternalHrUser: isInternalHrUserByEmail(company.email),
        // custom intercom attributes to track active company
        current_company: company.name,
        current_company_subscription_plan: company.currentWelfarePlan,
        Canale: company.companyChannel?.name ?? null,
      });
    }
  };

  const watchVerticalPadding = () => {
    if (!isServiceEnabled(ExternalService.Intercom)) return;
    if (!ENV_VARS.intercom.appId) return;

    watch(hasActiveWizard, (isActive) => {
      update({ vertical_padding: getVerticalPadding(isActive) });
    });
  };

  return {
    initSupportChat,
    watchVerticalPadding,
  };
};
